/* eslint no-console:0 */

import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider, useDispatch, useSelector } from 'react-redux';
import './styles.css';
import { createMuiTheme, ThemeProvider } from '@material-ui/core/styles';
import { CssBaseline, useMediaQuery } from '@material-ui/core';
// import GA from 'react-ga4';
import {
  BrowserRouter as Router,
  Switch,
  Route,
  useLocation,
} from 'react-router-dom';
import uuid from 'uuid';

import { setModels } from './store/modelActions';
import { setElements, messageElement } from './store/elementActions';
import { setWorkers } from './store/workerActions';
import configureStore from './store/configureStore';
import MiniDrawer from './Components/MiniDrawer';
import './Components/dashboard/assets/css/material-dashboard-react.css';
import GTPressuraRegularWoff2 from './fonts/GTPressura-Regular.woff2';
import {
  getAuthDataAsync,
  setAuthDataAsync,
  setEdgeAiStudioYaml,
  loadEnvironment,
} from './Blocks/LocalStorage';
import {
  saveToApiServerAsync,
  getEndpointUrl,
  getEndpointHost,
  efetch,
} from './Components/ApiServerInterface';
import { appSignIn } from './api/auth.api';
import LoginMain from './Components/LoginMain';
import ErrorBoundary from './Components/ErrorBoundary';
import config from './config';
import {
  setAsGuest,
  clearAsGuest,
  setAsEnterprise,
  clearAsEnterprise,
  setAppKey,
  clearAppKey,
  setMobileAppMode,
  isMobileAppMode,
} from './utils/localStorage';

// GA.initialize(config.GA_MEASUREMENT_ID, {});

const gtPressura = {
  fontFamily: 'gtpressura',
  fontStyle: 'normal',
  fontDisplay: 'swap',
  fontWeight: 400,
  src: `
    local('GTPressura'),
    local('GTPressura-Regular'),
    url(${GTPressuraRegularWoff2}) format('woff2')
  `,
  // unicodeRange:
  //   'U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF',
};

const muiTheme = isLight => {
  const themeType = isLight ? 'light' : 'dark';
  const primaryLayerColor = isLight ? '#ffffff' : '#333333';
  const secondaryLayerColor = isLight ? '#a3a3a3' : '#333333';
  const tertiaryLayerColor = isLight ? '#d6d6d6' : '#474747';
  const typoColour = isLight ? '#1f1f1f' : '#ffffff';

  return createMuiTheme({
    typography: {
      fontFamily: 'GTPressura, Arial',
    },
    palette: {
      type: themeType,
      primary: {
        main: primaryLayerColor,
        secondary: secondaryLayerColor,
        tertiary: tertiaryLayerColor,
      },
      grey: {
        50: isLight ? '#fafafa' : '#303030',
      },
      component: {
        borderOutline: isLight ? '1px solid rgba(0, 0, 0, 0.12)' : '1px solid rgba(255, 255, 255, 0.12)',
        loginButton: !isLight ? '#333333' : 'rgba(0, 0, 0, 0.1) !important',
      },
    },
    appBarSpacing: '72px',
    hrefLink: {
      textColor: isLight ? '#000000' : '#FFFFFF',
    },
    typo: {
      themeColour: typoColour,
    },
    progressBarColor: isLight ? '#a3a3a3 !important' : 'white !important',
    // TO-DO: Bring theme to top layer so it applies to all components
    overrides: {
      MuiCssBaseline: {
        '@global': {
          '@font-face': [gtPressura],
        },
      },
      MuiSvgIcon: {
        root: {
          color: `${typoColour} !important`,
        },
      },
      MuiIconButton: {
        root: {
          color: typoColour,
        },
      },
      MuiTypography: {
        root: {
          color: typoColour,
        },
      },
      MuiSelect: {
        root: {
          color: typoColour,
          backgroundColor: primaryLayerColor,
        },
      },
      MuiInputBase: {
        root: {
          color: typoColour,
        },
      },
      MuiInputLabel: {
        root: {
          color: typoColour,
        },
      },
      MuiOutlinedInput: {
        notchedOutline: {
          borderColor: isLight ? 'rgba(31, 31, 31, 0.7) !important' : 'rgba(255, 255, 255, 0.7) !important',
        },
      },
      MuiFormLabel: {
        root: {
          color: typoColour,
          '&.Mui-focused': {
            color: typoColour,
          },
        },
      },
      MuiSlider: {
        root: {
          color: typoColour,
        },
      },
      PrivateValueLabel: {
        label: {
          color: 'white',
        },
      },
    },
  });
};

const store = configureStore();

function App() {
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(null);
  const [registered, setRegistered] = useState(false);
  const [isAzureMarketplaceSubscribe, setIsAzureMarketplaceSubscribe] = useState(false);
  const [azureMarketplaceToken, setAzureMarketplaceToken] = useState(null);
  const [authData, setAuthData] = useState(null);
  const themes = React.useRef({
    lightTheme: muiTheme(true),
    darkTheme: muiTheme(false),
  });
  const systemTheme = useMediaQuery('(prefers-color-scheme: light)');
  const [theme, setTheme] = useState('system-theme');

  const messageDispatcher = async event => {
    // console.debug('messageDispatcher');
    if (event.data.type === 'app-sign-in') {
      const appAuthData = JSON.parse(event.data.appAuthData);
      console.debug(appAuthData);
      await handleAppSignIn(appAuthData);
    }
    // console.debug('messageDispatcher completed');
  };

  // const query = new URLSearchParams(useLocation().search);
  const handleAppSignIn = async appAuthData => {
    // console.debug('handleAppSignIn');
    await appSignIn(appAuthData);
    // console.debug('handleAppSignIn completed');
  };

  const handleOnThemeChange = selectedTheme => setTheme(selectedTheme);

  const muiThemeObj = React.useMemo(() => {
    switch (theme) {
      case 'system-theme':
        return systemTheme ? themes.current.lightTheme : themes.current.darkTheme;
      case 'light':
        return themes.current.lightTheme;
      case 'dark':
        return themes.current.darkTheme;
      default:
        return new Error('Unknown theme.');
    }
  }, [theme, systemTheme]);

  useEffect(() => {
    window.addEventListener('message', messageDispatcher, false);
    // console.debug(window.location.search);

    (async () => {
      if (window.location.search === '?app-key=Ju5pVxHP9C') {
        await setAuthDataAsync(null);
      }

      if (window.location.search === '?cmd=set-mobile-app-mode') {
        // console.debug('set-mobile-app-mode');
        // const configKey = 'configMinimal';
        // const profile = config.APP.PROFILES[configKey].AUTHDATA;
        // setAuthData(profile);
        // await setAuthDataAsync(profile);
        // setAppKey(null);
        // await setEdgeAiStudioYaml(profile.settings);
        await setMobileAppMode(true);
        // console.debug('set-mobile-app-mode completed');
      }

      if (window.location.search === '?cmd=clear-mobile-app-mode') {
        // console.debug('clear-mobile-app-mode');
        await setMobileAppMode(false);
      }

      if (isMobileAppMode()) {
        const configKey = 'configMinimal';
        const profile = config.APP.PROFILES[configKey].AUTHDATA;
        await setEdgeAiStudioYaml(profile.settings);
      }

      if (window.location.pathname === '/register/azure') {
        setIsAzureMarketplaceSubscribe(true);

        // Some test links here:
        // https://portal.azure.com/?microsoft_azure_marketplace_ItemHideKey=75e75ddc-4424-4154-a2e3-24b8cf217500#blade/Microsoft_Azure_Marketplace/GalleryItemDetailsBladeNopdl/preview/undefined/product/%7B%22displayName%22%3A%22EdgeAI%20Studio%20(Local)%22%2C%22itemDisplayName%22%3A%22EdgeAI%20Studio%20(Local)%22%2C%22id%22%3A%22crholdingslimited1632123976225.edgeai-studio-local-preview%22%2C%22bigId%22%3A%22DZH318Z09TMF%22%2C%22offerId%22%3A%22edgeai-studio-local-preview%22%2C%22publisherId%22%3A%22crholdingslimited1632123976225%22%2C%22publisherDisplayName%22%3A%22C%26R%20Holdings%20Limited%22%2C%22longSummary%22%3A%22Some%20search%20summary%20here%22%2C%22description%22%3A%22Provide%20details%20to%20be%20displayed%20in%20the%20marketplace%20including%20descriptions%20of%20your%20offer%20and%20marketing%20assets.%20To%20create%20engaging%20marketplace%20offers%20read%20documents.%26nbsp%3BLearn%20more%20about%26nbsp%3Bit%20there.%22%2C%22isPrivate%22%3Afalse%2C%22hasPrivateOffer%22%3Afalse%2C%22isMacc%22%3Afalse%2C%22isPreview%22%3Atrue%2C%22isByol%22%3Afalse%2C%22isCSPEnabled%22%3Afalse%2C%22isCSPSelective%22%3Afalse%2C%22isThirdParty%22%3Atrue%2C%22isReseller%22%3Afalse%2C%22hasFreeTrials%22%3Afalse%2C%22marketingMaterial%22%3A%5B%5D%2C%22legalTermsUri%22%3A%22https%3A%2F%2Fquery.prod.cms.rt.microsoft.com%2Fcms%2Fapi%2Fam%2Fbinary%2FRWOdz2%22%2C%22privacyPolicyUri%22%3A%22https%3A%2F%2Fstudio.cnr.ai%2Fterms-of-service.pdf%22%2C%22version%22%3A%22f8c524b0-6d79-4b4d-9b42-eccd51047bc3%22%2C%22metadata%22%3A%7B%22leadGeneration%22%3A%7B%22productId%22%3Anull%7D%2C%22testDrive%22%3Anull%7D%2C%22categoryIds%22%3A%5B%22ArtificialIntelligence%22%2C%22ai-for-business%22%2C%22ml-operations%22%2C%22developer-tools-saas%22%2C%22scripts%22%2C%22tools-developer-tools%22%2C%22azureCertified%22%2C%22fromPublishingPortal%22%5D%2C%22screenshotUris%22%3A%5B%22https%3A%2F%2Fstore-images.s-microsoft.com%2Fimage%2Fapps.4766.694db921-c37c-4518-8007-13493707fe82.36f7b193-dbf5-40c5-9a8a-68469d619f3b.26e011ee-ac6e-45b2-8e07-af16a2c3cf22%22%5D%2C%22videos%22%3A%5B%5D%2C%22links%22%3A%5B%7B%22id%22%3A%22C%26R%22%2C%22displayName%22%3A%22C%26R%22%2C%22uri%22%3A%22https%3A%2F%2Fcnr.ai%2F%22%7D%5D%2C%22filters%22%3A%5B%5D%2C%22plans%22%3A%5B%7B%22id%22%3A%220001%22%2C%22displayName%22%3A%22Business%20Plan%20(Local)%22%2C%22description%22%3A%22Describe%20the%20plan%20as%20you%20would%20like%20it%20to%20appear%20in%20the%20marketplace.%22%2C%22restrictedAudience%22%3A%7B%7D%2C%22skuId%22%3A%220001%22%2C%22planId%22%3A%22business-20211027%22%2C%22legacyPlanId%22%3A%22crholdingslimited1632123976225.edgeai-studio-local-previewbusiness-20211027%22%2C%22keywords%22%3A%5B%5D%2C%22type%22%3A%22SaaS%22%2C%22leadGeneration%22%3A%7B%22productId%22%3A%22crholdingslimited1632123976225.edgeai-studio-local-preview%22%7D%2C%22testDrive%22%3Anull%2C%22availabilities%22%3A%5B%5D%2C%22categoryIds%22%3A%5B%5D%2C%22conversionPaths%22%3A%5B%220002%22%5D%2C%22metadata%22%3A%7B%7D%2C%22uiDefinitionUri%22%3A%22https%3A%2F%2Fquery.prod.cms.rt.microsoft.com%2Fcms%2Fapi%2Fam%2Fbinary%2FRWOdyZ%22%2C%22artifacts%22%3A%5B%7B%22name%22%3A%22UiDefinition.json%22%2C%22uri%22%3A%22https%3A%2F%2Fcatalogartifact.azureedge.net%2Fpublicartifacts%2Fcrholdingslimited1632123976225.edgeai-studio-local-preview-f8c524b0-6d79-4b4d-9b42-eccd51047bc3%2FUiDefinition.json%22%2C%22type%22%3A%22Custom%22%7D%2C%7B%22name%22%3A%22DefaultTemplate%22%2C%22uri%22%3A%22https%3A%2F%2Fcatalogartifact.azureedge.net%2Fpublicartifacts%2Fcrholdingslimited1632123976225.edgeai-studio-local-preview-f8c524b0-6d79-4b4d-9b42-eccd51047bc3%2FArtifacts%2FmainTemplate.json%22%2C%22type%22%3A%22Template%22%7D%2C%7B%22name%22%3A%22Documents_studio-screenshot-tmp.pdf%22%2C%22uri%22%3A%22https%3A%2F%2Fcatalogartifact.azureedge.net%2Fpublicartifacts%2Fcrholdingslimited1632123976225.edgeai-studio-local-preview-f8c524b0-6d79-4b4d-9b42-eccd51047bc3%2FArtifacts%2FDocuments%2Fstudio-screenshot-tmp.pdf%22%2C%22type%22%3A%22Custom%22%7D%2C%7B%22name%22%3A%22SampleImage_apps.4766.694db921-c37c-4518-8007-13493707fe82.36f7b193-dbf5-40c5-9a8a-684
        // https://studio-uat.cnr.ai/register?token=ONZ8tjHbgXri%2Bj5aMxh06SIDvhYn72dZjUFlQzn53k4YQTWY9eKSfiTvl6x%2FmJ125OLk0YdMkfy%2Fw5Sf%2BK3PoLC3VJglgJYGbijjLfdJqrqdtMTNUJlBFRnwSNbmNVfXdBTaBGQj8QnpR4Cl%2Bnj%2FxzVN%2FhMMs3ZYmpo%2F4t3VLFqoqiDw%2BKMtFgTjCQUVfqrEewGo7XgXSwofjoAlze0DRK9N7qxbOhmpZ%2FCNy1KUOGI%3D
        // http://localhost:3000/register/azure?token=JcBjFZf09WG8yFKJPPE7FWYd2JTLyENlDcgT3d%2FQ8g0Oyv72pMKOMJeKHOnwSvQAgd6pPX%2B8cWiSt3uZk23JmfVpOyeyM0siwxGeOCM%2BzijI24Zqtqx6BLi5gbcB49c9%2BDEZoQttU8NMvgWHgtY6V%2FW4RX2HAimTMl1V2MdUqRuKUNEvMs3Ksno46yLUWFTqWFcX7CQWR%2FXyGyDzraizduOOqoET6iAyqmJqa8UEdVVm2raoHO1ZyWmZfsZQtI%2Bl
        // Signup from Azure marketplace
        const url = new URL(window.location);
        const token = new URLSearchParams(url.search).get('token');
        if (!token) {
          alert('Azure marketplace token missing');
          document.location.href = 'https://studio.cnr.ai';
          return;
        }
        setAzureMarketplaceToken(token);

        // It is a valid landing from Azure marketplace.  We clear the login.
        await setAuthDataAsync(null);

        setLoading(false);

        // We now wait for user to do authentication with Microsoft to ensure
        // the email matches and permissions granted to use our app.

        return;
      }

      const data = await getAuthDataAsync();
      if (data) {
        if (data.theme) setTheme(data.theme);
        if (data.key) {
          setRegistered(true);
        }
        setView('main');
        // GA.event({ category: 'nav', action: 'main' });
      }
      else {
        setView('login');
        // GA.event({ category: 'nav', action: 'login' });
      }
      setLoading(false);
      // GA.send({ hitType: 'pageview', page: window.location.pathname + window.location.search });
    })();
  }, []);

  const handleOnSuccess = async user => {
    // console.debug(user);
    setAuthData(user);
    if (user.theme) handleOnThemeChange(user.theme);
    await setAuthDataAsync(user);
    await setEdgeAiStudioYaml(user.settings);
    const env = await loadEnvironment();
    if (user.key) {
      if (isAzureMarketplaceSubscribe) {
        await activateAzureSubscription();
      } else {
        clearAsGuest();
        clearAsEnterprise();
        setRegistered(true);
        setView('main');
      }
    } else {
      setRegistered(false);
      // GA.event({ category: 'nav', action: 'registering' });
      setView('register');
    }
  };

  const handleSignInWithAppKey = async appKey => {
    const profile = config.APP.PROFILES[appKey].AUTHDATA;
    setAuthData(profile);
    setAppKey(appKey);
    await setAuthDataAsync(profile);
    await setEdgeAiStudioYaml(profile.settings);
    if (profile.key) {
      setRegistered(true);
      setView('main');
    }
    else {
      setRegistered(false);
      // GA.event({ category: 'nav', action: 'registering' });
      setView('register');
    }
  };

  const handleSignInWithMicrosoft = async () => {
    // GA.event({ category: 'click', action: 'ms_sign_in' });
    const width = 600;
    const height = 600;
    const left = (window.innerWidth / 2) - (width / 2);
    const top = (window.innerHeight / 2) - (height / 2);
    const url = '/auth/microsoft';

    return window.open(url, '',
      `toolbar=no, location=no, directories=no, status=no, menubar=no, 
    scrollbars=no, resizable=no, copyhistory=no, width=${width}, 
    height=${height}, top=${top}, left=${left}`);
  };

  const activateAzureSubscription = async () => {
    await loadEnvironment();

    // Call API to activate the account
    const requestId = uuid.v4();
    const requestCorrelationId = uuid.v4();
    try {
      const response = await efetch(`${getEndpointUrl()}/partners/azure/activate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          requestId,
          requestCorrelationId,
          marketplaceToken: azureMarketplaceToken,
        }),
      });
      if (response.ok) {
        const r = await response.json();
        // console.debug(JSON.stringify(r, ' ', 2));
        window.location.href = '/';
      }
      else if (typeof response.json === 'function') {
        const errorJson = await response.json();
        const errorCode = errorJson?.error?.code || '';
        if (errorCode === 'azure-saas-activation-active-plan-exists') {
          alert('An activated plan already exists for this email address.  If you intend to switch to another plan, please use the Change Plan option in Azure Marketplace.');
          window.location.href = '';
        } else {
          alert(JSON.stringify(errorJson, ' ', 2));
        }
      } else {
        const errorText = await response.text();
        console.error(errorText);
        alert(errorText);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleOnRegisterSuccess = async () => {
    setRegistered(true);
    // GA.event({ category: 'nav', action: 'registered' });
    if (isAzureMarketplaceSubscribe) {
      await activateAzureSubscription();
    } else {
      window.location.reload();
    }
  };

  return (
    <Router>
      <ErrorBoundary>
        <Provider store={store}>
          <ThemeProvider theme={muiThemeObj}>
            <CssBaseline />
            {loading
              ? <></>
              : false}
            {!loading && (view === 'main' || view === 'register')
              ? <div className="App">
                <MiniDrawer
                  authData={authData}
                  registered={registered}
                  onRegisterSuccess={handleOnRegisterSuccess}
                  onThemeChange={handleOnThemeChange} />
              </div>
              : false}
            {!loading && view === 'login'
              ? <div className="Login" style={{ height: '100%' }}>
                <LoginMain
                  onSuccess={handleOnSuccess}
                  handleSignInWithAppKey={handleSignInWithAppKey}
                  handleSignInWithMicrosoft={handleSignInWithMicrosoft}
                  useCase="general"
                />
              </div>
              : false}
            {!loading
              ? <Switch>
                <Route path="/register/azure">
                  <LoginMain
                    onSuccess={handleOnSuccess}
                    handleSignInWithMicrosoft={handleSignInWithMicrosoft}
                    useCase="azure-signup"
                  />
                </Route>
              </Switch>
              : null
            }
          </ThemeProvider>
        </Provider>
      </ErrorBoundary>
    </Router>
  );
}

// const loadConfig = async () => {
//   let yaml = await getEdgeAiStudioYaml();
//   if (yaml && yaml.trim().length === 0) yaml = null;
//   await setEdgeAiStudioYaml(yaml || defaultConfig);
// };

(async () => {
  try {
    const worker = new Worker('/static/worker.js');

    store.subscribe(() => {
      // console.debug('store state = ', store.getState());

      // send elements to worker
      // debugger; // eslint-disable-line no-debugger
      const serializableElements = store.getState().elements.map(r => {
        if (!r) return null;
        return {
          position: r.position,
          sourcePosition: r.sourcePosition,
          targetPosition: r.targetPosition,
          opType: r.opType,
          type: r.type,
          label: r.label,
          id: r.id,
          source: r.source,
          target: r.target,
        };
      }).filter(r => !!r);

      // console.debug(serializableElements);

      worker.postMessage(['set', { key: 'elements', value: serializableElements }]);
    });

    worker.addEventListener('message', e => {
      // console.debug('worker.addEventListener message received');
      // debugger; // eslint-disable-line no-debugger
      const targetId = store.getState().elements.find(r => !!r.target).target;
      // console.debug('targetId', targetId);
      // store.dispatch(messageElement(targetId, e.data));
    });

    // TODO: Store should be no longer used. Try to remove them.
    // await store.dispatch(setModels());
    // await store.dispatch(setElements([]));
    // await store.dispatch(setWorkers([{
    //   id: 'pipeline',
    //   worker,
    // }]));

    // await loadConfig();
  }
  catch (e) {
    console.error(e);
    alert('We are not able to connect to the EdgeAI internal system. Please reload.'); // eslint-disable-line no-alert
    // window.location.reload();
    return;
  }

  const rootElement = document.getElementById('root');
  const root = createRoot(rootElement);
  root.render(<App />);

  window.notifyAppSignIn = appAuthData => {
    // console.debug('window.notifyAppSignIn');
    // console.debug(appAuthData);
    window.postMessage({ type: 'app-sign-in', appAuthData }, '*');
    // console.debug('window.notifyAppSignIn completed');
  };
})();
